import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { HSP_CONTACT_VALUES, HSP_REGISTRATION_VALUES } from '../utils/constants'

export interface HspPlusValues {
  orderNumber: string
  lastName: string
  zipCode: string
  email: string
  mobileNumber: string
  phoneType: string
}

export const defaultHspPlusValues: HspPlusValues = {
  orderNumber: '',
  lastName: '',
  zipCode: '',
  email: '',
  mobileNumber: '',
  phoneType: '',
}

export const getStoredHspValues = (): HspPlusValues => {
  try {
    const reg = JSON.parse(sessionStorage.getItem(HSP_REGISTRATION_VALUES) ?? '{}')
    const contact = JSON.parse(sessionStorage.getItem(HSP_CONTACT_VALUES) ?? '{}')
    return { ...defaultHspPlusValues, ...reg, ...contact }
  } catch {
    return defaultHspPlusValues
  }
}

export const hasRegistrationInfo = (values: HspPlusValues): boolean => {
  return Boolean(values.orderNumber.trim() && values.lastName.trim() && values.zipCode.trim())
}

export interface HspPlusContextType {
  values: HspPlusValues
  setValues: (updates: Partial<HspPlusValues>) => void
  clearValues: () => void
}

export const defaultHspPlusContext: HspPlusContextType = {
  values: defaultHspPlusValues,
  setValues: () => undefined,
  clearValues: () => undefined,
}

export const HspPlusContext = createContext<HspPlusContextType>(defaultHspPlusContext)

const HspPlusProviderInternal: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const previousPathname = useRef<string | null>(null)
  const startsRegistration = pathname.endsWith('/hsp-plus') || pathname.endsWith('/hsp-plus/registration')
  const [values, setValuesState] = useState<HspPlusValues>(() => (startsRegistration ? defaultHspPlusValues : getStoredHspValues()))

  const setValues = useCallback((updates: Partial<HspPlusValues>) => {
    setValuesState((prev) => {
      const next = { ...prev, ...updates }
      try {
        sessionStorage.setItem(HSP_REGISTRATION_VALUES, JSON.stringify({ orderNumber: next.orderNumber, lastName: next.lastName, zipCode: next.zipCode }))
        sessionStorage.setItem(HSP_CONTACT_VALUES, JSON.stringify({ email: next.email, mobileNumber: next.mobileNumber, phoneType: next.phoneType }))
      } catch {
        // Ignore storage errors
      }
      return next
    })
  }, [])

  const clearValues = useCallback(() => {
    try {
      sessionStorage.removeItem(HSP_REGISTRATION_VALUES)
      sessionStorage.removeItem(HSP_CONTACT_VALUES)
    } catch {
      // Ignore
    }
    setValuesState(defaultHspPlusValues)
  }, [])

  // Clear stale values when a new registration starts, but preserve them when returning from contact information (using the back button).
  useEffect(() => {
    const returnedFromContactInformation = previousPathname.current?.endsWith('/hsp-plus/contact-information')
    if (startsRegistration && !returnedFromContactInformation) clearValues()
    previousPathname.current = pathname
  }, [pathname, startsRegistration, clearValues])

  // If the contact information screen is reached without previous registration info, redirect back to the start of the flow
  useEffect(() => {
    if (pathname.includes('/hsp-plus/contact-information') && !hasRegistrationInfo(values)) {
      navigate('/hsp-plus/registration', { replace: true })
    } else if (pathname.includes('/hsp-plus/otp') && !hasRegistrationInfo(values)) {
      navigate('/hsp-plus/registration', { replace: true })
    } else if (pathname.includes('/hsp-plus/otp') && !values.email.trim()) {
      navigate('/hsp-plus/contact-information', { replace: true })
    }
  }, [pathname, values, navigate])

  return <HspPlusContext.Provider value={{ values, setValues, clearValues }}>{children}</HspPlusContext.Provider>
}

export const HspPlusProvider: FC<PropsWithChildren> = ({ children }) => {
  const existingContext = useContext(HspPlusContext)
  if (existingContext !== defaultHspPlusContext) {
    return <>{children}</>
  }
  return <HspPlusProviderInternal>{children}</HspPlusProviderInternal>
}

export const useHspPlusContext = () => useContext(HspPlusContext)
export default HspPlusProvider
